import {
	Question,
	MediaObj,
	ClientQuestion,
	Score,
	StudentAnswer,
	ClassScore,
} from '@mission.io/question-toolkit'
export { validateEvery as validate } from './validate'
import { QuestionVariant, QuestionHelpers, MediaType } from './definitions'
export {
	MediaType,
	getImageDropdownKey,
	parseTemplateStringIntoSegments,
	convertTemplateSegmentsToTemplateString,
	getTemplateKeys,
	TemplateSegmentType,
} from './definitions'
export { getQuestionModel as getModel } from './model'
export * from './constants'
import { Schema as MongooseSchema } from 'mongoose'
import { Root } from 'mdast'
import {
	getIndicesOfClosestSelectableElements,
	selectWholeElementRangeTransformer,
	selectWithinElementRangeTransformer,
} from './helpers/annotations/rangeTransformer'
import { gradeAnnotations } from './helpers/annotations/gradeAnnotations'
import { getVisibleWordsFromAst } from './helpers/annotations/astReader'
import { parseMarkdownToAST } from './helpers/annotations'

export const variantTypes = QuestionVariant

/**
 * Get individual schema for a question type, does not include the question type. This is used to create a new question or to include
 * a question in a different form of document.
 * @param {QuestionVariant} questionType
 * @param {typeof MongooseSchema} schema schema used to get the mongo schema
 * @returns
 */
export function getIndividualSchema(
	questionType: QuestionVariant,
	schema: typeof MongooseSchema
): MongooseSchema {
	return QuestionHelpers[questionType].getSchema(schema)
}

/**
 * Checks if a question answer is correct
 *
 * @return {Score | undefined}
 *  score.score :  0 < score < 1 if partially correct
 *				   0 if completely incorrect
 *				   1 if completely correct
 *                 null if unknown (QuestionVariant.FREE_RESPONSE)
 *  score.perfectScore: the highest score that can scored on this question
 *
 *  undefined if bad data (question or answer does not exist, or the answer is invalid)
 */
export function grade(
	question: Question,
	answer: StudentAnswer,
	options?: { markdownAST?: Root }
): Score | undefined {
	let score = undefined
	if (!answer) return undefined
	if (QuestionHelpers[question.type]) {
		score = QuestionHelpers[question.type].grade(question, answer, options)
		const bestPossibleScore = getBestPossibleScore(question)
		if (score === undefined) return undefined
		return { score, perfectScore: bestPossibleScore }
	}
	return undefined
}

/**
 * Finds and replaces all media items in the given question using the provided callback function.
 * A shallow copy of the question will be returned, and the original question and any of its nested properties
 * will not be mutated.
 */
export function updateMedia(
	_question: Question,
	callback: (media: MediaObj) => MediaObj
): Question {
	const question = { ..._question }
	if (question.media) {
		question.media = question.media.map(mediaItem => callback(mediaItem))
	}
	if (QuestionHelpers[question.type]) {
		return QuestionHelpers[question.type].updateMedia(question, callback)
	}
	return question
}

/**
 * Adds all media files on the questions provided to a FormData object provided.
 * Also gives names to each file that references where the url for each media needs
 * to be inserted back into the questions list. See decodeMediaReferences for how that is done.
 * @param {unknown} questions
 * @param {FormData} formData
 */
export function encodeMediaFiles(
	questions: ClientQuestion[],
	formData: FormData
): void {
	searchForMedia(questions, '', formData)
}

/**
 * Takes a list of `mediaReferences` for question media objects, and inserts urls into each media object in the list
 * of questions provided. The `referenceKey` in a media reference is formed in the above method, `encodeMediaFiles`.
 * We expect that a front end application calls `encodeMediaFiles` and the server calls `decodeMediaReferences`, after
 * uploading question media files to a cloud service making a urls accessible for the media objects on a question
 * @param questions
 * @param mediaReferences
 */
export function decodeMediaReferences(
	questions: ClientQuestion[],
	mediaReferences: { url: string; referenceKey: string }[]
): void {
	mediaReferences.forEach(({ referenceKey, url }) => {
		const paths = referenceKey.split('.')
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let reference: any = questions
		paths.forEach((path, index) => {
			const potentialIndex = parseInt(path, 10)
			const lookup = isNaN(potentialIndex) ? path : potentialIndex
			if (!reference[lookup]) {
				throw Error(
					`Reference ${referenceKey} was not formed properly when media references were encoded, or questions have changed since last encoding.`
				)
			}
			if (index + 1 === paths.length) {
				if (paths[index - 1] !== 'media') {
					throw Error(
						`${referenceKey} does not point to a media object in an array of media objects.`
					)
				}
				if (reference[lookup].type !== MediaType.IMAGE) {
					throw Error(
						`Support for referencing a media type other than ${MediaType.IMAGE} is not implemented in decodeMediaReferences (reference key: ${referenceKey})`
					)
				}
				reference[lookup] = {
					type: MediaType.IMAGE,
					url,
				}
			} else {
				reference = reference[lookup]
			}
		})
	})
}

/**
 * getCorrectAnswerForQuestion - get the correct answer for a given question
 *
 * @param  {ClientQuestion} question - the question to get the answer for
 *
 * @returns StudentAnswer | void - the correct answer for the question (null/undefined) if could not generate the answer
 */
export function getCorrectAnswerForQuestion(
	question: ClientQuestion
): StudentAnswer | void {
	return QuestionHelpers[question.type].getCorrectAnswer(question)
}

/**
 * getClassScores - get the class scores for the question from the student answers
 *
 * @param {ClientQuestion} question - the question to get the class score for
 * @param {StudentAnswer[]} answers - the student answers for the given question
 *
 * @returns ClassScore | void - get the class scores for the question (null/undefined) if could not generate the class scores
 */
export function getClassScores(
	question: ClientQuestion,
	answers: StudentAnswer[]
): ClassScore | void {
	return QuestionHelpers[question.type].getClassScores(question, answers)
}

export const CLOSE_ANNOTATION_HELPERS = {
	rangeTransformers: {
		selectWholeElementRangeTransformer,
		selectWithinElementRangeTransformer,
	},
	gradeAnnotations,
	selectableRangesGenerators: {
		getVisibleWordsFromAst,
	},
	parseMarkdownToAST,
	getIndicesOfClosestSelectableElements,
}

/**
 * Recursive function: looks through an entity (at the root a list of questions)
 * for all media objects and adds any files that may be on the media to a provided FormData object.
 * Also uses a provided lookup string to reference the location of the media within the entity.
 * @param {unknown} entity at the root of recursion, this entity is a list of questions
 * @param {string} lookupStr the string will represent where a media file is located:
 *  i.e. Given a list of questions = [
 * 		{...Question1, choices: [Choice1, {...Choice2, media: [Media1]}]},
 * 		{...Question2, media: [Media2]}
 * ]
 * Lookup '0.choices.1.media.0' refers to Media1 and '1.media.0' refers to Media2
 * @param {FormData} formData a FormData object that allows us to append a media file. The media will be appended using the key 'files'
 * @returns {void}
 */
function searchForMedia(
	entity: unknown,
	lookupStr: string,
	formData: FormData
) {
	if (!entity) return
	if (Array.isArray(entity)) {
		entity.forEach((value, index) =>
			searchForMedia(value, lookupStr + `${index}.`, formData)
		)
		return
	}
	if (typeof entity === 'object' && entity !== null && entity !== undefined) {
		const obj = entity as Record<string, unknown>
		Object.keys(obj).forEach(key => {
			if (key === 'media') {
				const mediaList = obj.media
				if (Array.isArray(mediaList)) {
					mediaList.forEach((media, mediaIndex) => {
						if (media.type === MediaType.IMAGE && media.file) {
							formData.append(
								'files',
								media.file,
								lookupStr + `media.${mediaIndex}`
							)
						}
					})
				}
			} else {
				searchForMedia(obj[key], lookupStr + `${key}.`, formData)
			}
		})
	}
	return
}

/**
 * Based off of question type, returns the best possible score a student can receive on this question.
 * @param {Question} question the question to get the best possible score for
 * @returns {number} the best possible score for the question
 */
function getBestPossibleScore(question: Question): number {
	if (question.type === QuestionVariant.CLOSE_READING) {
		return QuestionHelpers[QuestionVariant.CLOSE_READING].getBestPossibleScore(
			question
		)
	}
	return question.scoreValue ?? 1
}
